/**
 * Pedidos (Productos del Pedido):
 * - Recoge en bodega + Xerox: solo `stock_xerox`.
 * - Recoge en bodega + Ecommerce: solo `stock_ecomerce` (SumaSaldo ecommerce).
 * - Sin recogida o sin elegir bodega: General = `stock_ecomerce` + GSM + temporal.
 *
 * Sin opciones de pedido (p. ej. catálogo legacy): `stock` + GSM + Distrito.
 * La API puede enviar negativos; se suman tal cual.
 */

export type OrderBodegaStockMode = 'xerox_only' | 'combined' | 'default' | 'ecommerce_only'

export type ProductStockWarehouseField =
  | 'stock_xerox'
  | 'stock_gsm'
  | 'stock_distrito'
  | 'stock_bodtemporal'

/** Ecommerce (typo en API: stock_ecomerce) */
export type ProductStockEcommerceField = 'stock_ecomerce' | 'stock_ecommerce'

/** Bodegas sumadas en pedidos (modo combined), sin Xerox ni Distrito. */
const ORDER_COMBINED_WAREHOUSE_FIELDS = ['stock_gsm', 'stock_bodtemporal'] as const

/** Legacy: listado catálogo sin modo pedido. */
const LEGACY_SUM_WAREHOUSE_FIELDS = ['stock_gsm', 'stock_distrito'] as const

export function parseProductStockField(raw: unknown): number {
  if (raw === undefined || raw === null) return 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

/** Saldo base del producto (sin bodegas adicionales por campo). */
export function getProductBaseSaldo(product: Record<string, unknown>): number {
  return parseProductStockField(product.stock ?? product.stock_quantity)
}

export function getProductEcommerceStock(product: Record<string, unknown>): number {
  return parseProductStockField(product.stock_ecomerce ?? product.stock_ecommerce)
}

export type ComputeProductDisplayStockOptions = {
  /** Solo catálogo legacy: suma ecommerce además de GSM + Distrito */
  includeEcommerce?: boolean
  /** Modo según bodega de recogida en pedidos */
  bodegaStockMode?: OrderBodegaStockMode
  /** @deprecated usar bodegaStockMode */
  xeroxBodegaOnly?: boolean
}

function resolveBodegaStockMode(
  options?: ComputeProductDisplayStockOptions
): 'xerox_only' | 'ecommerce_only' | 'combined' | 'legacy_default' {
  if (options?.bodegaStockMode === 'xerox_only' || options?.xeroxBodegaOnly) {
    return 'xerox_only'
  }
  if (options?.bodegaStockMode === 'ecommerce_only') {
    return 'ecommerce_only'
  }
  if (
    options?.bodegaStockMode === 'combined' ||
    options?.bodegaStockMode === 'default'
  ) {
    return 'combined'
  }
  if (options?.bodegaStockMode) {
    return 'combined'
  }
  return 'legacy_default'
}

/** Pedidos modo general: ecommerce + GSM + temporal (sin `stock` base ni Xerox/Distrito). */
export function computeOrderCombinedStock(product: Record<string, unknown>): number {
  let total = getProductEcommerceStock(product)
  for (const field of ORDER_COMBINED_WAREHOUSE_FIELDS) {
    total += parseProductStockField(product[field])
  }
  return total
}

function computeLegacyCatalogStock(
  product: Record<string, unknown>,
  options?: ComputeProductDisplayStockOptions
): number {
  let total = getProductBaseSaldo(product)
  for (const field of LEGACY_SUM_WAREHOUSE_FIELDS) {
    total += parseProductStockField(product[field])
  }
  if (options?.includeEcommerce) {
    total += getProductEcommerceStock(product)
  }
  return total
}

/** Recoge en bodega activo y bodega Xerox (id interno 95). */
export function isRecogeEnBodegaXerox(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): boolean {
  return recogeEnBodega && bodegaRecogidaSeleccion === '95'
}

/** Recoge en bodega activo y bodega Ecommerce. */
export function isRecogeEnBodegaEcommerce(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): boolean {
  return recogeEnBodega && bodegaRecogidaSeleccion === 'ecommerce'
}

export function getOrderBodegaStockMode(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): OrderBodegaStockMode {
  if (isRecogeEnBodegaXerox(recogeEnBodega, bodegaRecogidaSeleccion)) {
    return 'xerox_only'
  }
  if (isRecogeEnBodegaEcommerce(recogeEnBodega, bodegaRecogidaSeleccion)) {
    return 'ecommerce_only'
  }
  return 'combined'
}

export function getOrderStockDisplayOptions(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): ComputeProductDisplayStockOptions {
  return {
    bodegaStockMode: getOrderBodegaStockMode(recogeEnBodega, bodegaRecogidaSeleccion)
  }
}

/** Cotizaciones y pantallas sin recogida en bodega: suma combined (sin Xerox ni Distrito). */
export function getCombinedStockDisplayOptions(): ComputeProductDisplayStockOptions {
  return { bodegaStockMode: 'combined' }
}

/** Etiqueta legible de la bodega para mensajes de validación de stock en pedidos. */
export function getRecogidaBodegaDisplayLabel(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): string {
  if (isRecogeEnBodegaXerox(recogeEnBodega, bodegaRecogidaSeleccion)) {
    return 'Bodega Xerox'
  }
  if (isRecogeEnBodegaEcommerce(recogeEnBodega, bodegaRecogidaSeleccion)) {
    return 'Bodega Ecommerce'
  }
  return 'General'
}

/**
 * Stock para mostrar/validar según modo de bodega de recogida.
 */
export function computeProductDisplayStock(
  product: Record<string, unknown> | null | undefined,
  options?: ComputeProductDisplayStockOptions
): number {
  if (!product) return 0
  const mode = resolveBodegaStockMode(options)
  if (mode === 'xerox_only') {
    return parseProductStockField(product.stock_xerox)
  }
  if (mode === 'ecommerce_only') {
    return getProductEcommerceStock(product)
  }
  if (mode === 'combined') {
    return computeOrderCombinedStock(product)
  }
  return computeLegacyCatalogStock(product, options)
}

/** Recalcula stock a mostrar desde campos crudos del producto. */
export function getProductDetailDisplayStock(
  detail: Record<string, unknown> | null | undefined,
  options?: ComputeProductDisplayStockOptions
): number {
  if (!detail) return 0
  return computeProductDisplayStock(detail, options)
}

/**
 * Normaliza respuesta de GET /products o /products/multiple:
 * deja `stock` y `stock_quantity` con el total y conserva campos por bodega.
 */
export function enrichProductWithDisplayStock<T extends Record<string, unknown>>(
  product: T,
  options?: ComputeProductDisplayStockOptions
): T & {
  stock: number
  stock_quantity: number
  display_stock: number
  stock_xerox: number
  stock_gsm: number
  stock_distrito: number
  stock_bodtemporal: number
  stock_ecomerce: number
} {
  const display = computeProductDisplayStock(product, options)
  return {
    ...product,
    stock: display,
    stock_quantity: display,
    display_stock: display,
    stock_xerox: parseProductStockField(product.stock_xerox),
    stock_gsm: parseProductStockField(product.stock_gsm),
    stock_distrito: parseProductStockField(product.stock_distrito),
    stock_bodtemporal: parseProductStockField(product.stock_bodtemporal),
    stock_ecomerce: getProductEcommerceStock(product)
  }
}

/** Recalcula stock mostrado en productDetails (p. ej. al cambiar recoge en bodega). */
export function reapplyOrderProductDetailsStock(
  details: Record<string, Record<string, unknown>>,
  lineItems: Array<{ sku?: string; product_id?: string; is_combo?: number; components?: Array<{ sku: string }> }> | undefined,
  options: ComputeProductDisplayStockOptions
): Record<string, Record<string, unknown>> {
  if (!details || Object.keys(details).length === 0) return details
  const next: Record<string, Record<string, unknown>> = {}
  for (const [key, raw] of Object.entries(details)) {
    const display = computeProductDisplayStock(raw, options)
    next[key] = { ...raw, stock: display, stock_quantity: display, display_stock: display }
  }
  lineItems?.forEach(item => {
    if (item.is_combo !== 1 || !item.components?.length) return
    const stocks: number[] = []
    item.components.forEach(component => {
      const comp = next[component.sku]
      stocks.push(comp ? computeProductDisplayStock(comp, options) : 0)
    })
    if (stocks.length === 0) return
    const minStock = Math.min(...stocks)
    const comboKey = item.sku || item.product_id
    if (comboKey && next[comboKey]) {
      next[comboKey] = {
        ...next[comboKey],
        stock: minStock,
        stock_quantity: minStock,
        display_stock: minStock
      }
    }
  })
  return next
}

/** Mapeo estándar tras GET /products o /products/multiple (pedidos, cotizaciones, etc.). */
export function mapApiProductToOrderDetails(
  product: Record<string, unknown>,
  options?: ComputeProductDisplayStockOptions
) {
  const enriched = enrichProductWithDisplayStock(product, options)
  const sku = String(enriched.sku ?? '').trim()
  return {
    ...enriched,
    sku,
    precio_ofima: product.price,
    iva_ofima: product.tax_rate,
    price: product.price,
    tax_rate: product.tax_rate ?? 0.19
  }
}
